import type { MfaMethod } from "@opensea/embedded-wallet-sdk"
import {
  cancelMfaChallenge,
  initMfaChallenge,
  submitMfaChallenge,
} from "@opensea/embedded-wallet-sdk"
import { proxy } from "valtio"
import { deepClone } from "valtio/utils"
import { UnreachableCaseError } from "../lib/errors"

// -- Helpers -------------------------------------------------------
const STORE_RESET_DELAY = 500

function getSubmissionErrorForReason(reason: MFASubmissionErrorReason) {
  switch (reason) {
    case "MfaVerificationFailed":
      return "Verification failed. Double-check your code and retry."
    case "MaxMfaRetries":
      return "You have exceeded the maximum number of attempts."
    case "MfaTimeout":
      return "The authentication code has expired."
    default:
      return "An unknown error occurred."
  }
}

// -- Types ---------------------------------------------------------
type MfaChallengeStepId =
  | "select-challenge-option"
  | "challenge-sms"
  | "challenge-totp"

type MfaChallengeStep = {
  id: MfaChallengeStepId
}

type MFASubmissionErrorReason =
  | "MfaVerificationFailed"
  | "MaxMfaRetries"
  | "MfaTimeout"
  | "Unknown"

type MfaChallengeStateType = {
  step: MfaChallengeStep
  submitting: boolean
  submissionError?: string
  initializingStep?: MfaChallengeStepId
}

// -- State ---------------------------------------------------------
const INITIAL_STATE: MfaChallengeStateType = {
  step: { id: "select-challenge-option" },
  submitting: false,
  submissionError: undefined,
  initializingStep: undefined,
}

export const MfaChallengeState = proxy<MfaChallengeStateType>(
  deepClone(INITIAL_STATE),
)

// -- Actions -------------------------------------------------------
export const MfaChallengeActions = {
  async selectChallengeOption(mfaMethod: MfaMethod) {
    if (MfaChallengeState.initializingStep !== undefined) {
      return
    }
    switch (mfaMethod) {
      case "sms":
        MfaChallengeState.initializingStep = "challenge-sms"
        await initMfaChallenge(mfaMethod)
        MfaChallengeState.step = { id: "challenge-sms" }
        MfaChallengeState.initializingStep = undefined
        break
      case "totp":
        MfaChallengeState.initializingStep = "challenge-totp"
        await initMfaChallenge(mfaMethod)
        MfaChallengeState.step = { id: "challenge-totp" }
        MfaChallengeState.initializingStep = undefined
        break
      case "passkey":
        break
      default:
        throw new UnreachableCaseError(mfaMethod)
    }
  },

  async submitChallenge(code: string) {
    const mfaMethod =
      MfaChallengeState.step.id === "challenge-sms" ? "sms" : "totp"
    MfaChallengeState.submitting = true
    try {
      await submitMfaChallenge(mfaMethod, code)
      setTimeout(() => {
        MfaChallengeActions.reset()
      }, STORE_RESET_DELAY)
      return true
    } catch (error) {
      if (
        typeof error === "object" &&
        error !== null &&
        "reason" in error &&
        typeof error.reason === "string"
      ) {
        MfaChallengeState.submissionError = getSubmissionErrorForReason(
          error.reason as MFASubmissionErrorReason,
        )
      } else {
        MfaChallengeState.submissionError = "Failed to authenticate."
      }
      MfaChallengeState.submitting = false
      return false
    }
  },

  reset() {
    MfaChallengeState.step = { id: "select-challenge-option" }
    MfaChallengeState.submitting = false
    MfaChallengeState.submissionError = undefined
    MfaChallengeState.initializingStep = undefined
  },

  async onBack() {
    await cancelMfaChallenge()
    MfaChallengeState.step = { id: "select-challenge-option" }
  },
}
